import type { StorageAdapter } from "../storage/adapter";
import type { TriageResult } from "../storage-schema";

export interface TriageStore {
  get(id: string): Promise<TriageResult | undefined>;
  getAll(): Promise<Record<string, TriageResult>>;
  set(id: string, result: TriageResult): Promise<void>;
  /** Keep only entries whose id is in `liveIds`; returns true if anything was pruned. */
  prune(liveIds: Iterable<string>): Promise<boolean>;
}

/** Per-notification triage cache over a StorageAdapter (mirrors createConversations in conversations/index.ts); all ops run on a promise-chain mutex so a concurrent set/prune can't lose a write via interleaved read-modify-write. */
export function createTriageStore(adapter: StorageAdapter): TriageStore {
  // Serialize every op so read-modify-write pairs can't interleave (set from the classify loop vs prune on eviction).
  let chain: Promise<unknown> = Promise.resolve();
  const serial = <T>(op: () => Promise<T>): Promise<T> => {
    const run = chain.then(
      () => op(),
      () => op()
    );
    chain = run.then(
      () => undefined,
      () => undefined
    );
    return run;
  };
  const readAll = async (): Promise<Record<string, TriageResult>> =>
    (await adapter.get("triageResults")).triageResults ?? {};
  return {
    get(id) {
      return serial(async () => (await readAll())[id]);
    },
    getAll() {
      return serial(readAll);
    },
    set(id, result) {
      return serial(async () => {
        const all = await readAll();
        await adapter.set({ triageResults: { ...all, [id]: result } });
      });
    },
    prune(liveIds) {
      return serial(async () => {
        const all = await readAll();
        const live = new Set(liveIds);
        const next: Record<string, TriageResult> = {};
        let changed = false;
        for (const [id, value] of Object.entries(all)) {
          if (live.has(id)) next[id] = value;
          else changed = true;
        }
        if (changed) await adapter.set({ triageResults: next });
        return changed;
      });
    },
  };
}
